<template>
  <div>

    <Head :title="form.name" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-primary-100 hover:text-primary-200" href="/servers">Server</Link>
      <span class="text-primary-100 font-medium">/</span>
      {{ form.name }}
    </h1>
    <trashed-message v-if="server.deleted_at" class="mb-6" @restore="restore"> This server has been deleted.
    </trashed-message>
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="update">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <text-input v-model="form.name" :error="form.errors.name" class="pb-8 pr-6 w-full lg:w-1/2" label="Name" />
          <text-input v-model="form.ip" :error="form.errors.ip" class="pb-8 pr-6 w-full lg:w-1/2" label="IP" />
        </div>
        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button v-if="!server.deleted_at" class="text-red-600 hover:underline" tabindex="-1" type="button"
            @click="destroy">Server löschen</button>
          <loading-button :loading="form.processing" class="btn-primary-100 ml-auto" type="submit">Server
            updaten</loading-button>
        </div>
      </form>
    </div>
    <!-- Tab List -->
    <ul role="tablist" class="-mb-px flex items-stretch gap-2 text-slate-500 mt-12">
      <!-- Tab 1 -->
      <li> <button @click="viewTableId = 1" :aria-selected="viewTableId === 1"
          :class="{ 'bg-primary-100 text-white': viewTableId === 1 }"
          class="flex gap-2 items-center rounded-full px-6 h-10 py-2 text-sm font-medium" role="tab"
          aria-selected="true">
          <icon :class="{ 'fill-white': viewTableId === 1 }" name="domains" class="block w-6 h-6 fill-gray-400" />
          <span>Domains ({{ domains.data.length || '0' }})</span>
        </button> </li>
      <!-- Tab 2 -->
      <li>
        <button @click="viewTableId = 2" :aria-selected="viewTableId === 2"
          :class="{ 'bg-primary-100 text-white': viewTableId === 2 }"
          class="flex gap-2 items-center rounded-full px-6 h-10 py-2 text-sm font-medium" role="tab"
          aria-selected="false">
          <icon :class="{ 'fill-white': viewTableId === 2 }" name="users" class="block w-6 h-6 fill-gray-400" />
          <span>Kunden ({{ server.customers.length || '0' }})</span>
        </button>
      </li>
    </ul>
    <div v-if="viewTableId === 1">
      <div class="flex items-center mb-6 mt-6">
        <search-filter :hideSearch="true" :title="'Sortierung'" v-model="form.search" class="" @reset="reset">
          <label class="block text-gray-700">Sortieren nach:</label>
          <select v-model="form.sortByCol" class="form-select mt-1 w-full">
            <option v-for="column in columns" :value="column"> {{ column }}</option>
          </select>
          <label class="block text-gray-700 mt-8">Anzahl der Ergebnisse:</label>
          <select v-model="form.itemsPerPage" class="form-select mt-1 mb-4 w-full">
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="75">75</option>
            <option value="100">100</option>
          </select>
        </search-filter>

        <search-filter v-model="form.search" class="mr-4 w-full max-w-md" @reset="reset">
          <label class="block text-gray-700">Aktiv:</label>
          <select v-model="form.active" class="form-select mt-1 mb-4 w-full">
            <option :value="null">Alle anzeigen</option>
            <option value="onlyAct">Nur aktive anzeigen</option>
            <option value="onlyInact">Nur inaktive anzeigen</option>
          </select>
        </search-filter>
      </div>

      <div class="mt-6 bg-white rounded shadow overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <tr class="text-left font-bold">
            <th class="pb-4 pt-6 px-6" v-for="column in columns">
              <a href="#" @click="sortBy(column)"
                :class="{ active: sortKey == column, desc: form.sortDirection == 'DESC' }">
                {{ column }}
              </a>
            </th>
          </tr>
          <tr v-for="domain in domains.data" :key="domain.id" class="hover:bg-gray-100 focus-within:bg-gray-100"
            :class="{ 'bg-gray-50': domain.active == 0 }">
            <td class="border-t">
              <Link class="flex items-center px-6 py-4 text-primary-200 focus:text-primary-200"
                :href="`/domains/${domain.id}/edit`">
              <icon v-if="!domain.active" name="inactive" class="flex-shrink-0 ml-2 w-3 h-3 fill-red-500" />
              <icon v-if="domain.active" name="active" class="flex-shrink-0 ml-2 w-3 h-3 fill-green-500" />
              <icon v-if="domain.flaged" name="flag" class="flex-shrink-0 ml-2 w-3 h-3 fill-yellow-500" />
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4 text-primary-100 focus:text-primary-200"
                :href="`/domains/${domain.id}/edit`">
              {{ domain.domain }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="`/domains/${domain.id}/edit`" tabindex="-1">
              {{ domain.due_date }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4 hover:text-primary-100"
                :href="`/customers/${domain.customer.id}/edit`" tabindex="-1">
              <div v-if="domain.customer">
                {{ domain.customer.name }}
              </div>
              </Link>
            </td>

            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="`/domains/${domain.id}/edit`" tabindex="-1">
              <span v-if="!domain.charged" class="text-red-500">Nein</span>
              <span v-if="domain.charged" class="text-green-500">Ja</span>
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="`/domains/${domain.id}/edit`" tabindex="-1">
              {{ domain.ordered_at }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="`/domains/${domain.id}/edit`" tabindex="-1">
              {{ domain.charged_till }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="`/domains/${domain.id}/edit`" tabindex="-1">
              {{ domain.amount_net }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="`/domains/${domain.id}/edit`" tabindex="-1">
              {{ domain.comment }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="`/domains/${domain.id}/edit`" tabindex="-1">
              {{ domain.package }}
              </Link>
            </td>
            <td class="w-px border-t">
              <Link class="flex items-center px-4" :href="`/domains/${domain.id}/edit`" tabindex="-1">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
              </Link>
            </td>
          </tr>
          <tr v-if="domains.data.length === 0">
            <td class="px-6 py-4 border-t" colspan="4">No Domain found.</td>
          </tr>
        </table>
      </div>
      <pagination class="mt-6" :links="domains.links" />

    </div>
    <div v-if="viewTableId === 2">
      <div class="mt-6 bg-white rounded shadow overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <tr class="text-left font-bold">
            <th class="pb-4 pt-6 px-6">Name</th>
            <th class="pb-4 pt-6 px-6" colspan="2">E-Mail</th>
          </tr>
          <tr v-for="customer in server.customers" :key="customer.id"
            class="hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="border-t">
              <Link class="flex items-center px-6 py-4 focus:text-indigo-500" :href="`/customers/${customer.id}/edit`">
              {{ customer.name }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="`/customers/${customer.id}/edit`" tabindex="-1">
              {{ customer.email }}
              </Link>
            </td>

            <td class="w-px border-t">
              <Link class="flex items-center px-4" :href="`/customers/${customer.id}/edit`" tabindex="-1">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
              </Link>
            </td>
          </tr>
          <tr v-if="server.customers.length === 0">
            <td class="px-6 py-4 border-t" colspan="4">No customers found.</td>
          </tr>
        </table>
      </div>
    </div>


  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import throttle from 'lodash/throttle'
import SearchFilter from '@/Shared/SearchFilter'
import pickBy from 'lodash/pickBy'
import Pagination from '@/Shared/Pagination'

export default {
  components: {
    Head,
    Icon,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    TrashedMessage,
    SearchFilter,
    Pagination
  },
  layout: Layout,
  props: {
    domains: Object,
    server: Object,
    filters: Object,
    itemsPerPage: Number,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        search: this.filters.search,
        name: this.server.name,
        ip: this.server.ip,
        itemsPerPage: 50
      }),
      viewTableId: 1,
      columns: [
        'Aktiv',
        'Domain',
        'fällig b. Reg.',
        'Besitzer',
        'Berechnet',
        'Bestellt am',
        'Berechnet bis',
        'Betrag/NETTO',
        'Bemerkung',
        'Paket'
      ],
    }
  },
  setup(props) {
    console.log('SETUP Server');
    console.log(props.server);
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get(`/servers/${this.server.id}/edit`, pickBy(this.form), { preserveState: true })
      }, 150),
    }
  },
  methods: {
    update() {
      this.form.put(`/servers/${this.server.id}`)
    },
    destroy() {
      if (confirm('Are you sure you want to delete this server?')) {
        this.$inertia.delete(`/servers/${this.server.id}`)
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this server?')) {
        this.$inertia.put(`/servers/${this.server.id}/restore`)
      }
    },
  },
}
</script>
